import { useEffect, useState } from "react";
import { Icon } from "../shell/Icon";

interface HealthResponse {
  ok: boolean;
  workspaceRoot: string;
}

interface State {
  status: "loading" | "ok" | "error";
  data?: HealthResponse;
  error?: string;
}

/**
 * Health-Page im Redesign-Look. Backend-Reachability + Workspace-Path +
 * Mock-Event-Log. Echte Event-Stream-Anbindung wäre Folge-Sprint.
 */
export function HealthPage() {
  const [state, setState] = useState<State>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    fetch("/api/health")
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return (await res.json()) as HealthResponse;
      })
      .then((data) => {
        if (!cancelled) setState({ status: "ok", data });
      })
      .catch((err) => {
        if (!cancelled) setState({ status: "error", error: String(err) });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isOk = state.status === "ok";

  return (
    <div className="screen fade-up">
      <div className="detail-hero">
        <div className="detail-hero-l">
          <div className="detail-id">
            <div className="uppercase-mini">/ HEALTH</div>
            <h1 className="detail-customer">System</h1>
            <div className="detail-meta mono">
              <span className="bv-chip">backend · {isOk ? "up" : state.status}</span>
              {state.status === "ok" && state.data && (
                <span className="id-chip">{state.data.workspaceRoot}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="metric-grid">
        <MetricCard
          label="BACKEND"
          value={isOk ? "UP" : state.status === "loading" ? "…" : "DOWN"}
          sub={isOk ? "API erreichbar" : state.error ?? "lade …"}
          accent={isOk}
        />
        <MetricCard
          label="WORKSPACE"
          value={
            state.status === "ok" && state.data
              ? state.data.workspaceRoot.split(/[/\\]/).pop() ?? "—"
              : "—"
          }
          sub="Daten-Wurzel"
        />
        <MetricCard label="PIPELINE" value="–" sub="Telemetrie folgt" />
        <MetricCard label="KOSTEN" value="–" sub="LLM-Budget folgt" />
      </div>

      <section className="block">
        <div className="block-head">
          <span className="uppercase-mini">// VERBINDUNG</span>
          <span className="block-sep" />
          <span
            className="mono"
            style={{
              fontSize: 11,
              color: isOk ? "var(--ok)" : "var(--bad)",
            }}
          >
            ● {isOk ? "ONLINE" : state.status.toUpperCase()}
          </span>
        </div>
        {state.status === "loading" && (
          <div className="notice-body">
            <div className="dim">Prüfe Verbindung zum Backend …</div>
          </div>
        )}
        {state.status === "ok" && state.data && (
          <div className="notice-body">
            <div>
              <span className="pill ok">
                <span className="dot" />
                ERREICHBAR
              </span>
            </div>
            <div className="kpi-row">
              <span>
                workspaceRoot:{" "}
                <span className="mono">{state.data.workspaceRoot}</span>
              </span>
            </div>
          </div>
        )}
        {state.status === "error" && (
          <div className="notice-body">
            <div className="warn-line" style={{ borderLeftColor: "var(--bad)", background: "oklch(0.72 0.18 25 / 0.08)" }}>
              <strong>Backend nicht erreichbar:</strong>{" "}
              <span className="mono">{state.error}</span>
            </div>
            <div className="dim">
              Tipp: Backend auf Port 3000 starten —{" "}
              <code
                className="mono"
                style={{
                  background: "var(--bg-elevated)",
                  padding: "2px 6px",
                  borderRadius: 4,
                }}
              >
                pnpm --filter @bidi-handel/backend run dev
              </code>
            </div>
          </div>
        )}
      </section>

      <section className="block">
        <div className="block-head">
          <span className="uppercase-mini">// EVENT LOG (MOCK)</span>
          <span className="block-sep" />
          <span className="uppercase-mini" style={{ color: "var(--fg-faint)" }}>
            Echte Telemetrie folgt
          </span>
        </div>
        <div className="health-log">
          {MOCK_LOG.map(([t, s, m], i) => (
            <div key={i} className="log-row" style={{ animationDelay: `${i * 30}ms` }}>
              <span className="mono log-t">{t}</span>
              <span className={`pill ${s === "ok" ? "ok" : "warn"}`}>
                <span className="dot" />
                {s.toUpperCase()}
              </span>
              <span className="log-m">{m}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function MetricCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub: string;
  accent?: boolean;
}) {
  return (
    <div className={`metric ${accent ? "acc" : ""}`}>
      <div className="metric-label uppercase-mini">{label}</div>
      <div className="metric-value mono" style={{ fontSize: 28 }}>
        {value}
      </div>
      <div className="metric-sub">{sub}</div>
      <div className="metric-deco" aria-hidden>
        <svg viewBox="0 0 80 80" width="80" height="80">
          <circle cx="40" cy="40" r="30" stroke="currentColor" strokeOpacity="0.06" fill="none" />
          <circle cx="40" cy="40" r="22" stroke="currentColor" strokeOpacity="0.1" fill="none" />
          <circle cx="40" cy="40" r="14" stroke="currentColor" strokeOpacity="0.16" fill="none" />
        </svg>
      </div>
    </div>
  );
}

const MOCK_LOG: Array<[string, "ok" | "warn", string]> = [
  ["heute", "ok", "Health-Endpoint erreichbar"],
  ["heute", "ok", "Frontend gegen Backend Port 3000 verbunden"],
  ["info", "warn", "Echte Event-Stream-Anbindung folgt (WebSocket oder SSE)"],
];

void Icon; // reserved for future event-row icons
